import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, PresenceContext, motion } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';

export interface WizardStep {
  id: string;
  title: string;
  icon?: LucideIcon;
  content: ReactNode;
  /** Validação antes de avançar; retorne false para bloquear. */
  validate?: () => boolean | Promise<boolean>;
}

export interface WizardLabels {
  cancel?: string;
  back?: string;
  next?: string;
  stepOf?: string;
  stepAria?: string;
  backToStep?: string;
}

const DEFAULT_LABELS: Required<WizardLabels> = {
  cancel: 'Cancelar',
  back: 'Voltar',
  next: 'Próximo',
  stepOf: '{{current}} de {{total}}',
  stepAria: 'Etapa {{current}} de {{total}}',
  backToStep: 'Voltar para: {{title}}',
};

function formatWizardLabel(template: string, vars: Record<string, string | number>) {
  return Object.entries(vars).reduce(
    (result, [key, value]) => result.replaceAll(`{{${key}}}`, String(value)),
    template,
  );
}

interface WizardProps {
  steps: WizardStep[];
  onFinish: () => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  finishLabel?: string;
  finishLoadingLabel?: string;
  /** Desabilita o botão da última etapa (ex.: resgate acima do disponível). */
  finishDisabled?: boolean;
  /** Desabilita o botão Próximo nas etapas intermediárias. */
  nextDisabled?: boolean;
  labels?: WizardLabels;
  hideFirstStepCancel?: boolean;
  compactStepper?: boolean;
  lockNavigationWhileSubmitting?: boolean;
  submittingOverlay?: ReactNode;
  /** Enter no teclado avança para o próximo step (exceto em textarea). */
  advanceOnEnter?: boolean;
  /** Barra de progresso no mobile + navegação fixa no rodapé. */
  mobileOptimized?: boolean;
  /** No desktop, permite voltar clicando em etapas já visitadas no stepper. */
  clickableStepper?: boolean;
  /**
   * Scroll interno no corpo do wizard (útil em dialog/painel com altura limitada).
   * Em páginas, deixe false para o wheel rolar o documento ao passar o cursor no form.
   */
  scrollableBody?: boolean;
  /** Índice controlado (0-based). Sem ele, o Wizard gerencia o estado internamente. */
  stepIndex?: number;
  /** Notifica o índice atual (0-based) a cada mudança de etapa. */
  onStepIndexChange?: (index: number) => void;
  className?: string;
}

/** Assistente multi-step animado (slide entre passos, validação por etapa). */
export function Wizard({
  steps,
  onFinish,
  onCancel,
  isSubmitting,
  finishLabel = 'Concluir',
  finishLoadingLabel,
  finishDisabled = false,
  nextDisabled = false,
  hideFirstStepCancel = false,
  compactStepper = false,
  lockNavigationWhileSubmitting = false,
  submittingOverlay,
  advanceOnEnter = false,
  mobileOptimized = false,
  clickableStepper = false,
  scrollableBody = false,
  labels: labelsProp,
  stepIndex: controlledIndex,
  onStepIndexChange,
  className,
}: WizardProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const isControlled = controlledIndex != null;
  const [[uncontrolledIndex, direction], setState] = useState<[number, number]>([0, 0]);
  const index = isControlled ? controlledIndex : uncontrolledIndex;
  const directionRef = useRef(0);
  const prevControlled = useRef(controlledIndex);
  const [keyboardInset, setKeyboardInset] = useState(0);

  useEffect(() => {
    if (!isControlled || controlledIndex == null) return;
    if (prevControlled.current === controlledIndex) return;
    const prev = prevControlled.current ?? 0;
    directionRef.current = controlledIndex >= prev ? 1 : -1;
    prevControlled.current = controlledIndex;
  }, [controlledIndex, isControlled]);

  // Safari (e Chrome sem resizes-content): teclado sobrepõe o viewport e
  // `fixed bottom-0` fica atrás. Compensa com visualViewport.
  useEffect(() => {
    if (!mobileOptimized) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const sync = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset((prev) => (Math.abs(prev - inset) < 1 ? prev : inset));
    };

    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    };
  }, [mobileOptimized]);

  const directionForAnim = isControlled ? directionRef.current : direction;
  const isLast = index === steps.length - 1;
  const current = steps[index] ?? steps[0];

  const setIndex = (next: number, dir: number) => {
    directionRef.current = dir;
    if (!isControlled) setState([next, dir]);
    onStepIndexChange?.(next);
  };

  const goNext = async () => {
    if (current.validate && !(await current.validate())) return;
    if (isLast) {
      await onFinish();
      return;
    }
    setIndex(index + 1, 1);
  };

  const goBack = () => {
    if (lockNavigationWhileSubmitting && isSubmitting) return;
    if (index > 0) setIndex(index - 1, -1);
  };

  const navLocked = lockNavigationWhileSubmitting && isSubmitting;

  const goToStep = (targetIndex: number) => {
    if (navLocked) return;
    if (targetIndex >= index || targetIndex < 0) return;
    setIndex(targetIndex, -1);
  };

  const lastButtonLabel = isSubmitting && finishLoadingLabel ? finishLoadingLabel : finishLabel;
  const progressPct = Math.round(((index + 1) / steps.length) * 100);

  const navigation = (
    <div
      className={cn(
        'flex items-center justify-between gap-2',
        mobileOptimized &&
          'fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-md sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none',
      )}
      style={
        mobileOptimized && keyboardInset > 0 ? { bottom: keyboardInset } : undefined
      }
    >
      {hideFirstStepCancel && index === 0 ? (
        <span className="w-16 sm:w-auto" />
      ) : (
        <Button
          type="button"
          variant="ghost"
          onClick={index === 0 ? onCancel : goBack}
          disabled={navLocked}
          className={cn(mobileOptimized && 'h-11 px-3')}
        >
          {index === 0 ? (
            labels.cancel
          ) : (
            <>
              <ChevronLeft className="size-4" /> {labels.back}
            </>
          )}
        </Button>
      )}
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatWizardLabel(labels.stepOf, { current: index + 1, total: steps.length })}
        </span>
        <Button
          type={advanceOnEnter ? 'submit' : 'button'}
          form={advanceOnEnter ? 'wizard-step-form' : undefined}
          onClick={advanceOnEnter ? undefined : () => void goNext()}
          loading={isLast && isSubmitting}
          disabled={
            (navLocked && !isLast) ||
            (isLast ? finishDisabled : nextDisabled)
          }
          className={cn(mobileOptimized && 'h-11 min-w-[7.5rem]')}
        >
          {isLast ? (
            lastButtonLabel
          ) : (
            <>
              {labels.next} <ChevronRight className="size-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        'flex flex-col gap-4 sm:gap-6',
        mobileOptimized && 'pb-[calc(5rem+env(safe-area-inset-bottom))] sm:pb-0',
        className,
      )}
    >
      {mobileOptimized ? (
        <div className="space-y-1.5 sm:hidden">
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <motion.div
              className="h-full rounded-full bg-primary"
              initial={false}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            />
          </div>
          <p className="text-center text-xs text-muted-foreground">
            {formatWizardLabel(labels.stepAria, { current: index + 1, total: steps.length })}
          </p>
        </div>
      ) : null}

      <ol
        className={cn(
          'flex w-full shrink-0 items-center',
          mobileOptimized ? 'hidden sm:flex' : 'flex',
        )}
      >
        {steps.map((step, i) => {
          const done = i < index;
          const active = i === index;
          const StepIcon = step.icon;
          const isLastStep = i === steps.length - 1;
          const isClickable = clickableStepper && done && !navLocked;
          const indicatorClass = cn(
            'grid shrink-0 place-items-center rounded-full border text-xs font-semibold transition-colors',
            compactStepper ? 'size-7 text-[10px]' : 'size-9',
            active && 'border-primary bg-primary text-primary-foreground shadow-soft',
            done && 'border-primary/40 bg-primary/15 text-primary',
            !active && !done && 'border-border text-muted-foreground',
            isClickable &&
              'cursor-pointer hover:border-primary hover:bg-primary/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          );
          const indicatorContent = done ? (
            <Check className={compactStepper ? 'size-3' : 'size-4'} />
          ) : StepIcon ? (
            <StepIcon className={compactStepper ? 'size-3' : 'size-4'} />
          ) : (
            i + 1
          );
          return (
            <li
              key={step.id}
              className={cn('flex min-w-0 items-center', !isLastStep && 'flex-1 basis-0')}
              aria-current={active ? 'step' : undefined}
            >
              <div className={cn('flex shrink-0 items-center', !compactStepper && 'gap-2.5')}>
                {isClickable ? (
                  <motion.button
                    type="button"
                    initial={false}
                    animate={{ scale: active ? 1.06 : 1 }}
                    transition={{ type: 'spring', stiffness: 360, damping: 24 }}
                    className={indicatorClass}
                    onClick={() => goToStep(i)}
                    aria-label={formatWizardLabel(labels.backToStep, { title: step.title })}
                    title={step.title}
                  >
                    {indicatorContent}
                  </motion.button>
                ) : (
                  <motion.span
                    initial={false}
                    animate={{ scale: active ? 1.06 : 1 }}
                    transition={{ type: 'spring', stiffness: 360, damping: 24 }}
                    className={indicatorClass}
                    title={step.title}
                  >
                    {indicatorContent}
                  </motion.span>
                )}
                {!compactStepper ? (
                  <span
                    className={cn(
                      'hidden text-sm font-medium sm:block',
                      active ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {step.title}
                  </span>
                ) : null}
              </div>
              {!isLastStep ? (
                <span
                  className={cn(
                    'h-px min-w-2 flex-1 overflow-hidden rounded-full bg-border',
                    compactStepper ? 'mx-1.5' : 'mx-3',
                  )}
                >
                  <motion.span
                    className="block h-full rounded-full bg-primary"
                    initial={false}
                    animate={{ width: done ? '100%' : '0%' }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  />
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>

      <div
        className={cn(
          // min-h-0 só no desktop: no mobile, o default (min-height:auto) impede o
          // flex de esmagar o conteúdo quando o teclado encolhe a viewport.
          'relative flex-1 p-0.5 lg:min-h-0',
          scrollableBody &&
            'thin-scrollbar overflow-x-hidden overflow-y-auto overscroll-contain',
        )}
      >
        {navLocked && submittingOverlay ? (
          submittingOverlay
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={current.id}
              initial={{ opacity: 0, ...(scrollableBody ? {} : { x: directionForAnim >= 0 ? 16 : -16 }) }}
              animate={{ opacity: 1, ...(scrollableBody ? {} : { x: 0 }) }}
              exit={{ opacity: 0, ...(scrollableBody ? {} : { x: directionForAnim >= 0 ? -12 : 12 }) }}
              transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              className={scrollableBody ? undefined : 'will-change-transform'}
            >
              {/*
                Isola o conteúdo do ciclo de presença: só este motion.div decide
                quando o passo sai. Sem isso, qualquer motion descendente
                (layoutId do SegmentedControl no meio da animação, loop
                infinito) segura o `mode="wait"` e o passo seguinte nunca monta.
              */}
              <PresenceContext.Provider value={null}>
                {advanceOnEnter ? (
                  <form
                    id="wizard-step-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void goNext();
                    }}
                  >
                    {current.content}
                  </form>
                ) : (
                  current.content
                )}
              </PresenceContext.Provider>
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      <div className="shrink-0">{navigation}</div>
    </div>
  );
}
